import React, { useState, useEffect } from "react";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";

import {
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { toast } from "react-hot-toast";
import {
  ExclamationTriangleIcon,
  TrashIcon,
  PlusIcon,
  DocumentIcon,
  InformationCircleIcon,
  PencilIcon,
} from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import ProConfirmation from "../../../utils/ProConfirmation";
import { generateRandomString } from "../../../Utils";
import { ChecklistDeviationDialog } from "./checklistDeviation";
import { debounce, set } from "lodash";
import DocumentsDialog from "../../documents/documents-dialog";
import { DocumentUploadSectionV5 } from "../../Media/DocumentUploadSectionV5";
import { useTranslation } from "react-i18next";
import {
  effectiveActionsForChecklistItem,
  itemRowResolved,
} from "./checklistResponseConfig";
import { ChecklistItemCustomFields } from "./ChecklistItemCustomFields";
import { ChecklistItemGuidance } from "./ChecklistItemGuidance";
import { inputFieldsComplete } from "./checklistInputFields";
import clsx from "clsx";

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const grid = 8;

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: "none",
  padding: 20,
  margin: `0 0 ${grid}px 0`,

  // change background colour if dragging
  background: isDragging ? "lightgrey" : null,

  // styles we need to apply on draggables
  ...draggableStyle,
});

export default function ChecklistItemLinesCompact(props) {
  const { t } = useTranslation();
  const {
    setFieldValue,
    values,
    isLoading,
    name,
    drag,
    editable,
    type,
    checklist,
    disabled,
    deviation,
    validationErrors,
  } = props;

  const rows = values?.items || props?.items;

  // Reordered items by updating their row_order
  const orderItems = (rows) => {
    const array = [];

    if (!rows) return;

    rows.map((row, i) => {
      // Ensure that the id exists before using it

      // Generate a random string (10 characters)
      const random = generateRandomString();

      const rowParams = {
        ...row,
        row_order: i,
        draggableId: random,
      };

      array.push(rowParams);
    });

    return array;
  };

  // Title column is only used when group_row is true
  const defaultRows = [
    {
      id: null,
      row_order: null,
      description: "",
    },
  ];

  const [items, setItems] = useState(orderItems(rows ? rows : defaultRows));
  const [inputFieldErrorIds, setInputFieldErrorIds] = useState(() => new Set());

  const checklistItemNotChecked = () => {
    // Checks all items in the array. All items but be true isApproved OR isDeviation
    const allApproved = items.every((item) => itemRowResolved(item));
    return allApproved;
  };

  useEffect(() => {
    const scrollPosition = window.scrollY; // Save the scroll position

    if (setFieldValue) {
      setFieldValue(name, items);
    }
    window.scrollTo(0, scrollPosition); // Restore the scroll position
  }, [items]);

  const addItemRow = (type) => {
    const newRow = {
      title: "",
      description: "",
      avvik: false,
      row_order: items.length,
      isDeviation: false,
      isApproved: false,
      documents: [],
    };
    const updatedItems = [...items, newRow];
    setItems(orderItems(updatedItems));
    // setItems((prevState) => [...prevState, newRow]);
  };

  const onItemRowChange = (e, data) => {
    // setItems(prevState => ({ ...prevState, [1][description]: 'test' }))
    if (typeof e === "function") {
      e.preventDefault();
    }

    let prevItems = [...items];
    let item = {
      ...prevItems[data.row_order],
      [e.target.name]: e.target.value || e.target.checked,
    };
    prevItems[data.row_order] = item;
    setItems(prevItems);
  };

  // Only approved or deviation can be true, not both
  const onDeviationChange = (e, data) => {
    const updatedItems = items.map((item) => {
      if (item.id === data.id || item.draggableId === data.draggableId) {
        return {
          ...item,
          isDeviation: e.target.checked,
          isApproved: false,
        };
      }
      return item;
    });

    setItems(updatedItems);
  };

  const [createDeviationDialogOpen, setCreateDeviationDialogOpen] =
    useState(false);

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const newItems = reorder(
      items,
      result.source.index,
      result.destination.index
    );
    setItems(orderItems(newItems));
  };

  const [selectedItemRow, setSelectedItemRow] = useState();

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const handleDeleteRow = () => {
    setDeleteConfirmationOpen(false);
    // Delete selectedItem row from array
    if (selectedItemRow) {
      const newArray = items.map((item) =>
        item.id === selectedItemRow ? { ...item, delete: true } : item
      );
      setItems(orderItems(newArray));
    }
  };

  const deleteRow = (id) => {
    setSelectedItemRow(id);
    setDeleteConfirmationOpen(true);
  };

  const UploaderCallback = (newDocuments, row) => {
    // Documents handler
    let prevItems = [...items];

    let item = {
      ...prevItems[row.row_order],
      documents: [...(prevItems[row.row_order].documents || []), ...newDocuments],
    };

    prevItems[row.row_order] = item;

    setItems(prevItems);
  };

  const delayedCallback = debounce(props.parentCallback, 300);

  useEffect(() => {
    // props.parentCallback(items);
    delayedCallback(items);
  }, [items]);

  const [selectedChecklistItem, setSelectedChecklistItem] = useState(null);

  const handleDeviation = (row) => {
    if (!deviation) {
      let item = {};
      // find item by id or row_order if id is null
      if (!row.id) {
        item = items.find((item) => item.id === row.id);
      } else {
        item = items.find((item) => item.row_order === row.row_order);
      }
      onDeviationChange({ target: { checked: true } }, item);
    } else {
      setCreateDeviationDialogOpen(true);
      setSelectedChecklistItem(row);
    }
  };

  const handleDeviationCreate = (data, checklistItem) => {
    // Find item by id
    const item = items.find((item) => item.id === checklistItem.id);

    // Data === Deviation
    // Add the deviation to the item and update items
    let prevItems = [...items];

    let newItem = {
      ...prevItems[item.row_order],
      deviation: data,
      response_code: "deviation",
      isDeviation: true,
      isApproved: false,
      is_not_applicable: false,
    };

    prevItems[item.row_order] = newItem;

    setItems(prevItems);
  };

  useEffect(() => {
    if (!createDeviationDialogOpen) {
      setTimeout(() => {
        setSelectedChecklistItem(null);
      }, 250);
    }
  }, [createDeviationDialogOpen]);

  const canUploadImage = (item) => {
    if (editable) {
      return false;
    }

    if (type === "checklist") {
      if (deviation) {
        return !!(item?.isApproved || item?.is_not_applicable);
      }
      return true;
    } else {
      return false;
    }
  };

  const isResponseSelected = (item, key) => {
    if (item.response_code === key) return true;
    if (!item.response_code) {
      if (
        key === "approved" &&
        item.isApproved &&
        !item.isDeviation &&
        !item.is_not_applicable
      )
        return true;
      if (key === "deviation" && item.isDeviation) return true;
      if (key === "na" && item.is_not_applicable) return true;
    }
    return false;
  };

  const requireInputFieldsBeforeOutcome = (item) => {
    if (inputFieldsComplete(item.input_fields, item.input_values)) {
      return true;
    }

    const errorKey = item.id ?? item.draggableId ?? item.row_order;
    setInputFieldErrorIds((prev) => new Set(prev).add(errorKey));
    toast.error(t("quality.checklist.item.customFieldsRequiredBeforeOutcome"));
    return false;
  };

  const handleInputValuesChange = (item, nextValues) => {
    const matchRow = (it) =>
      (item.id != null && it.id === item.id) ||
      it.draggableId === item.draggableId ||
      it.row_order === item.row_order;

    setItems((prev) =>
      prev.map((it) =>
        matchRow(it) ? { ...it, input_values: nextValues } : it
      )
    );

    const errorKey = item.id ?? item.draggableId ?? item.row_order;
    setInputFieldErrorIds((prev) => {
      if (!prev.has(errorKey)) return prev;
      const next = new Set(prev);
      next.delete(errorKey);
      return next;
    });
  };

  const onResponseSelect = (item, key) => {
    const matchRow = (it) =>
      (item.id != null && it.id === item.id) ||
      it.draggableId === item.draggableId ||
      it.row_order === item.row_order;

    if (key === "deviation") {
      handleDeviation(item);
      return;
    }

    const clear = item.response_code === key;
    if (!clear && !requireInputFieldsBeforeOutcome(item)) {
      return;
    }

    const patch = clear
      ? {
          response_code: null,
          isApproved: false,
          isDeviation: false,
          is_not_applicable: false,
        }
      : key === "performed" || key === "approved"
        ? {
            response_code: key,
            isApproved: true,
            isDeviation: false,
            is_not_applicable: false,
          }
        : key === "na"
          ? {
              response_code: "na",
              isApproved: false,
              isDeviation: false,
              is_not_applicable: true,
            }
          : {};

    setItems((prev) =>
      prev.map((it) => (matchRow(it) ? { ...it, ...patch } : it))
    );
  };

  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);

  const [selectedChecklistItemDocumentId, setSelectedChecklistItemDocumentId] =
    useState(null);

  // Add a row if not rows available in the items array
  useEffect(() => {
    if (items.length === 0) {
      addItemRow();
    }
  }, []);

  // New state for dialogs
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [selectedItemForDialog, setSelectedItemForDialog] = useState(null);
  const [tempDescription, setTempDescription] = useState("");

  const handleDescriptionSave = () => {
    if (selectedItemForDialog) {
      onItemRowChange(
        { target: { name: "description", value: tempDescription } },
        selectedItemForDialog
      );
    }
    setDescriptionDialogOpen(false);
    setSelectedItemForDialog(null);
    setTempDescription("");
  };

  const openDescriptionDialog = (item) => {
    setSelectedItemForDialog(item);
    setTempDescription(item.description || "");
    setDescriptionDialogOpen(true);
  };

  const openInfoDialog = (item) => {
    setSelectedItemForDialog(item);
    setInfoDialogOpen(true);
  };

  const itemRow = (item, provided, snapshot) => {
    if (item.delete) return null; // Hide the item if it is marked as deleted

    return (
      <div
        key={"div" + item.id}
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden mb-3"
      >
        {/* Header with drag handle and delete button */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
          <div className="flex items-center space-x-2">
            {drag && (
              <div className="text-gray-400">
                <DragHandleIcon className="w-4 h-4" />
              </div>
            )}
            <div className="text-xs text-gray-500 font-medium">
              {/* Title with Info Icon */}
              <div className="flex items-center justify-between w-full">
                <div className="flex-1 mr-2">
                  {!editable && item?.id ? (
                    <div className="text-sm font-medium text-gray-900">
                      {item?.title}
                    </div>
                  ) : (
                    <TextField
                      disabled={disabled}
                      label="Title"
                      variant="outlined"
                      size="small"
                      value={item?.title}
                      fullWidth
                      name="title"
                      onChange={(e) => onItemRowChange(e, item)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "0.875rem",
                        },
                      }}
                    />
                  )}
                </div>

                {/* Info Button - Right side of title */}
                <button
                  type="button"
                  onClick={() => !disabled && openInfoDialog(item)}
                  disabled={disabled}
                  className={`flex-shrink-0 p-1.5 transition-colors duration-200 rounded ${
                    disabled
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                  }`}
                  title="Information"
                >
                  <InformationCircleIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {/* Document requirement warning - Right side */}
            {item?.documents_required && item?.documents?.length === 0 && (
              <div className="text-xs text-red-500 italic">
                ⚠️ Documents required for this item
              </div>
            )}

            {props.deleteRow && (
              <button
                onClick={() => !disabled && deleteRow(item.id)}
                disabled={disabled}
                className={`p-1 transition-colors duration-200 rounded ${
                  disabled
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-400 hover:text-red-600 hover:bg-red-50"
                }`}
              >
                <TrashIcon className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>

        {item?.description && disabled && (
        <div className="p-4">
          {/* Description */}
          <div className="text-sm text-gray-500">
            {item?.description}
          </div>
        </div>
        )}

        <ChecklistItemGuidance guidance={item?.guidance} className="mx-4 mt-3" />

        <ChecklistItemCustomFields
          fields={item?.input_fields}
          values={item?.input_values || {}}
          disabled={disabled}
          showErrors={inputFieldErrorIds.has(
            item.id ?? item.draggableId ?? item.row_order
          )}
          onChange={(nextValues) => handleInputValuesChange(item, nextValues)}
        />

        {/* Content */}
        <div className="p-4 space-y-3">
          {/* Action Buttons */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              {/* Description Button */}
              <button
                type="button"
                onClick={() => !disabled && openDescriptionDialog(item)}
                disabled={disabled}
                className={`p-1.5 transition-colors duration-200 rounded ${
                  disabled
                    ? "text-gray-300 cursor-not-allowed"
                    : "text-gray-400 hover:text-green-600 hover:bg-green-50"
                }`}
                title="Edit Description"
              >
                <PencilIcon className="h-4 w-4" />
              </button>

              {/* Upload Documents Button */}
              {!disabled && canUploadImage(item) && (
                <div
                  className={`p-1 rounded border-2 ${
                    item?.documents_required && item?.documents?.length === 0
                      ? "border-red-300 bg-red-50"
                      : item?.documents?.length > 0
                      ? "border-green-300 bg-green-50"
                      : "border-gray-200"
                  }`}
                >
                  <DocumentUploadSectionV5
                    required={
                      (item?.documents?.length || 0) === 0
                        ? item?.documents_required
                        : false
                    }
                    multiple={true}
                    key={`upload-${item.id}`}
                    disabled={isLoading}
                    module="checklist_item"
                    moduleParam={item?.id}
                    parentCallback={(data) => UploaderCallback(data, item)}
                    buttonStyle="compact"
                  />
                </div>
              )}

              {/* View Documents Button with Counter - Always accessible */}
              { !editable && (
              <div
                className={`relative p-1 rounded border-2 ${
                  item?.documents_required && item?.documents?.length === 0
                    ? "border-red-300 bg-red-50"
                    : item?.documents?.length > 0
                    ? "border-green-300 bg-green-50"
                    : "border-gray-200"
                }`}
              >
                <button
                  type="button"
                  onClick={() => {
                    setSelectedChecklistItemDocumentId(item.id);
                    setDocumentsDialogOpen(true);
                  }}
                  className="p-1.5 transition-colors duration-200 rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                  title={`View Documents (${item?.documents?.length || 0})`}
                >
                  <DocumentIcon className="h-4 w-4" />
                  {item?.documents?.length > 0 && (
                    <span className="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                      {item?.documents?.length}
                    </span>
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Validation Errors */}
            {(validationErrors && (
              validationErrors[`items.${item.row_order}`] ||
              validationErrors[`items.${items.findIndex(i => i.id === item.id || i.draggableId === item.draggableId)}`]
            )) && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="h-4 w-4 text-red-400 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="text-xs text-red-700">
                    {(() => {
                      const errorKey = validationErrors[`items.${item.row_order}`] 
                        ? `items.${item.row_order}`
                        : `items.${items.findIndex(i => i.id === item.id || i.draggableId === item.draggableId)}`;
                      const itemErrors = validationErrors[errorKey];
                      return (
                        <>
                          {itemErrors?.description && (
                            <div>{itemErrors.description}</div>
                          )}
                          {itemErrors?.documents && (
                            <div>{itemErrors.documents}</div>
                          )}
                          {itemErrors?.input_fields && (
                            <div>{itemErrors.input_fields}</div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            )}

            {/* Status Buttons - Right side */}
            <div className="flex items-center space-x-2">
              {/* Show status tags when disabled, buttons when enabled */}
              {disabled ? (
                <>
                  {item?.isApproved && (
                    <span className="inline-flex items-center rounded-full bg-green-100 px-3 py-1.5 text-xs font-medium text-green-800 border border-green-200">
                      ✓ Godkjent
                    </span>
                  )}
                  {item?.isDeviation && (
                    <span className="inline-flex items-center rounded-full bg-red-100 px-3 py-1.5 text-xs font-medium text-red-800 border border-red-200">
                      ⚠️ Avvik
                    </span>
                  )}
                  {item?.is_not_applicable && (
                    <span className="inline-flex items-center rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-800 border border-slate-200">
                      {t("quality.checklist.actions.na")}
                    </span>
                  )}
                  {!item?.isApproved &&
                    !item?.isDeviation &&
                    !item?.is_not_applicable && (
                    <span className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200">
                      Ikke sjekket
                    </span>
                  )}
                </>
              ) : (
                <>
                  {canUploadImage(item) && type === "checklist" && (
                    <div className="flex flex-wrap items-center justify-end gap-1.5">
                      {effectiveActionsForChecklistItem(
                        item,
                        checklist?.checklist_type
                      ).map((key) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => {
                            if (key === "deviation") {
                              if (item.isDeviation) {
                                onDeviationChange(
                                  { target: { checked: false } },
                                  item
                                );
                              } else if (requireInputFieldsBeforeOutcome(item)) {
                                handleDeviation(item);
                                setTimeout(
                                  () => openDescriptionDialog(item),
                                  100
                                );
                              }
                              return;
                            }
                            onResponseSelect(item, key);
                          }}
                          className={clsx(
                            "rounded border px-2.5 py-1 text-xs font-semibold transition-colors",
                            isResponseSelected(item, key)
                              ? "border-teal-600 bg-teal-600 text-white"
                              : "border-gray-200 bg-white text-gray-600 hover:border-gray-300"
                          )}
                        >
                          {t(`quality.checklist.actions.${key}`)}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <ResourceLoading />;
  }

  return (
    <div className="space-y-4">
      {!editable && !checklistItemNotChecked() && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-4 w-4 text-red-400" />
            </div>
            <div className="ml-2">
              <h3 className="text-xs font-medium text-red-800">
                Checklist is not ready
              </h3>
              <div className="mt-1 text-xs text-red-700">
                <p>Make sure to check all checklist items</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {items?.map((item, index) => (
                  <Draggable
                    isDragDisabled={!drag}
                    key={"drag" + item.id}
                    draggableId={item.draggableId}
                    index={index}
                  >
                    {(provided, snapshot) => itemRow(item, provided, snapshot)}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>

      {/* Add New Row Button */}
      {props.addRow && !disabled && editable && (
        <div className="flex justify-center pt-2">
          <button
            disabled={disabled}
            className="inline-flex items-center px-4 py-2 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            type="button"
            onClick={() => addItemRow()}
          >
            <PlusIcon className="w-3 h-3 mr-1" />
            Add New Item
          </button>
        </div>
      )}

      {/* Description Dialog */}
      <Dialog
        open={descriptionDialogOpen}
        onClose={() => setDescriptionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 3200 }}
      >
        <DialogTitle className="text-sm font-medium">
          Edit Description
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={tempDescription}
            onChange={(e) => setTempDescription(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDescriptionDialogOpen(false)}
            className="bg-gray-500 hover:bg-gray-600 text-white border-gray-500 hover:border-gray-600"
          >
            Cancel
          </Button>
          <Button onClick={handleDescriptionSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Info Dialog */}
      <Dialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{ zIndex: 3200 }}
      >
        <DialogTitle className="text-sm font-medium">
          Item Information
        </DialogTitle>
        <DialogContent>
          <div className="space-y-3">
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">
                {selectedItemForDialog?.title}
              </h4>
              <p className="text-sm text-gray-600">
                {selectedItemForDialog?.info_description ||
                  "No additional information available."}
              </p>
            </div>
            {selectedItemForDialog?.description && (
              <div>
                <h5 className="text-xs font-medium text-gray-700 mb-1">
                  Current Description:
                </h5>
                <p className="text-sm text-gray-600">
                  {selectedItemForDialog.description}
                </p>
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setInfoDialogOpen(false)}
            className="bg-gray-500 hover:bg-gray-600 text-white border-gray-500 hover:border-gray-600"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={"Are you sure you want to delete this row?"}
        action="delete"
        module="order_item"
        yesCallback={() => handleDeleteRow(selectedItemRow)}
        cancelCallback={() => setDeleteConfirmationOpen(false)}
      />

      <ChecklistDeviationDialog
        open={createDeviationDialogOpen}
        onClose={() => setCreateDeviationDialogOpen(false)}
        checklistItem={selectedChecklistItem}
        checklist={checklist}
        onRefresh={(data, checklistItem) =>
          handleDeviationCreate(data, checklistItem)
        }
      />

      <DocumentsDialog
        open={documentsDialogOpen}
        onClose={() => setDocumentsDialogOpen(false)}
        module="checklist_item"
        moduleParam={selectedChecklistItemDocumentId}
        onRefresh={props.onRefresh}
      />
    </div>
  );
}

ChecklistItemLinesCompact.defaultProps = {
  name: "items",
  drag: true,
  delete: true,
  editable: true,
  type: "checklist",
  disabled: false,
  deviation: true,
};
